import { forwardRef } from "react";
import { cn } from "@/lib/cn";
import { CheckboxCheck } from "./checkbox-check";

/**
 * Checkbox — 16px box with the shared animated check glyph.
 * Pair it with `<label htmlFor={id}>` for its visible name.
 */
type CheckboxProps = Omit<React.ComponentProps<"button">, "onChange"> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ checked, onCheckedChange, disabled, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={disabled ? undefined : () => onCheckedChange(!checked)}
      className={cn(
        "denker-checkbox-motion flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-[5px] border transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
        "disabled:pointer-events-none disabled:opacity-40",
        checked
          ? "border-accent bg-accent text-black"
          : "border-muted bg-input-fill text-transparent hover:border-accent",
        className,
      )}
      {...props}
    >
      <CheckboxCheck checked={checked} className="h-2.5 w-2.5" />
    </button>
  ),
);
Checkbox.displayName = "Checkbox";

export { Checkbox };
export type { CheckboxProps };
